import React, { useState } from 'react';
import { axiosInstance } from '../../App';
import { toast } from 'sonner';
import {
  Sparkles, X, AlertTriangle, ShieldCheck, Lightbulb, Loader2, Check,
} from 'lucide-react';


/**
 * AI Suggested Match drawer (Wave 2-D-3).
 *
 *  * Asks `POST /api/bank-recon/lines/{id}/ai-suggest`.
 *  * Only opens when the line's top deterministic confidence is in
 *    the [0.50, 0.70) band (caller responsibility).
 *  * Renders the LLM pick + Arabic reasoning + evidence chips.
 *  * User must explicitly accept — clicking "اقبل الاقتِراح" calls the
 *    existing manual-match endpoint. No auto-apply.
 *
 * Props:
 *  - line:        the bank line doc (must include id)
 *  - candidates:  the suggestions array (used to look up the picked candidate)
 *  - onClose
 *  - onAccepted(candidate_id)
 */


function ConfidenceBadge({ level }) {
  const tones = {
    high: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    med:  'bg-amber-100 text-amber-800 border-amber-200',
    low:  'bg-slate-100 text-slate-700 border-slate-200',
  };
  const labels = { high: 'ثِقَة عالِيَة', med: 'ثِقَة مُتَوَسِّطَة', low: 'ثِقَة مُنخَفِضَة' };
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold border rounded-full px-2 py-0.5 ${tones[level] || tones.low}`}>
      <ShieldCheck className="w-3 h-3" />
      {labels[level] || level}
    </span>
  );
}


function EvidenceChip({ ev }) {
  const tones = {
    high: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    med:  'bg-amber-50 text-amber-900 border-amber-200',
    low:  'bg-slate-50 text-slate-800 border-slate-200',
  };
  return (
    <div className={`rounded-lg border p-2 text-xs ${tones[ev.weight] || tones.low}`}
         data-testid={`ev-${ev.signal}`}>
      <div className="flex items-center gap-1 mb-0.5">
        <span className="font-mono font-bold text-[11px]">{ev.signal}</span>
        <span className="text-[9px] uppercase opacity-70">· {ev.weight}</span>
      </div>
      <div className="text-[11px] opacity-90">{ev.detail}</div>
    </div>
  );
}


export default function AIMatchSuggestion({ line, candidates = [], onClose, onAccepted }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const ask = async () => {
    setBusy(true);
    try {
      const { data } = await axiosInstance.post(
        `/bank-recon/lines/${line.id}/ai-suggest`);
      setData(data);
      if (data.available === false) {
        toast.message(`الذَّكاء غَير مُتاح: ${data.reason || ''}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل الاستِشارَة');
    } finally { setBusy(false); }
  };

  // Auto-ask on open
  React.useEffect(() => { if (line?.id) ask(); /* eslint-disable-next-line */ }, [line?.id]);

  const pickedCandidate = data?.pick_candidate_id
    ? candidates.find((c) => c.candidate_id === data.pick_candidate_id ||
                                c.id === data.pick_candidate_id)
    : null;

  const accept = async () => {
    if (!data?.pick_candidate_id) return;
    setAccepting(true);
    try {
      await axiosInstance.post(`/bank-recon/lines/${line.id}/manual-match`,
        { candidate_id: data.pick_candidate_id,
          kind: pickedCandidate?.candidate_kind || pickedCandidate?.source_type });
      toast.success('تَم قَبول اقتِراح الذَّكاء وَتَطبيق المُطابَقَة');
      onAccepted?.(data.pick_candidate_id);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل تَطبيق الاقتِراح');
    } finally { setAccepting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         data-testid="ai-match-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl" dir="rtl">
        <div className="flex items-center justify-between px-5 py-3 border-b bg-gradient-to-l from-indigo-50 to-white">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            <div>
              <div className="text-base font-bold">اقتِراح الذَّكاء الاصطِناعي</div>
              <div className="text-[10px] text-slate-500">
                مُساعِد فَقَط · لا تَطبيق تِلقائي · النِّطاق 50–70%
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="ai-close"
                  className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {busy ? (
            <div className="flex items-center justify-center py-10 text-slate-500 text-sm">
              <Loader2 className="w-5 h-5 animate-spin me-2" />
              يَستَشير الذَّكاء الاصطِناعي…
            </div>
          ) : !data ? (
            <div className="text-center text-slate-400 text-sm py-10">لا يُوجَد رَدّ.</div>
          ) : data.available === false ? (
            <div className="rounded-xl border bg-amber-50 border-amber-200 p-4 text-sm" data-testid="ai-unavailable">
              <div className="flex items-center gap-2 font-bold text-amber-900">
                <AlertTriangle className="w-4 h-4" />
                الذَّكاء غَير مُتاح لِهَذا السَّطر
              </div>
              <div className="text-xs text-amber-800 mt-2">
                السَّبَب: <code className="font-mono">{data.reason}</code>
                {data.top_confidence != null && (
                  <span> · ثِقَة المُحَرِّك: <b>{Math.round(data.top_confidence * 100)}%</b></span>
                )}
              </div>
              <div className="text-[11px] text-amber-700 mt-2">
                الذَّكاء يَعمَل فَقَط عِندَما تَكون الثِّقَة الحَتمِيَّة بَين 50% و 70%.
                خارِج هَذا النِّطاق، إِما المُطابَقَة تَلقائِيَّة (&ge;70%) أَو تَحتاج مُراجَعَة بَشَرِيَّة (&lt;50%).
              </div>
            </div>
          ) : (
            <>
              <div className="rounded-xl border bg-indigo-50/40 border-indigo-200 p-3"
                   data-testid="ai-pick-block">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-indigo-600" />
                    <div className="text-sm font-bold text-indigo-900">
                      {data.pick_candidate_id ? 'اختِيار الذَّكاء' : 'الذَّكاء غَير مُتَأَكِّد'}
                    </div>
                  </div>
                  <ConfidenceBadge level={data.self_confidence} />
                </div>
                {pickedCandidate ? (
                  <div className="text-xs space-y-0.5">
                    <div><b>المُرَشَّح:</b> <code className="font-mono">{pickedCandidate.id || data.pick_candidate_id}</code></div>
                    {pickedCandidate.supplier_name && <div><b>المورد:</b> {pickedCandidate.supplier_name}</div>}
                    {pickedCandidate.amount != null && <div><b>المَبلَغ:</b> <span className="font-mono">{pickedCandidate.amount}</span></div>}
                    {pickedCandidate.transaction_date && <div><b>التاريخ:</b> <span className="font-mono">{pickedCandidate.transaction_date}</span></div>}
                  </div>
                ) : data.pick_candidate_id ? (
                  <div className="text-xs font-mono text-indigo-900">
                    id: {data.pick_candidate_id}
                  </div>
                ) : (
                  <div className="text-xs text-slate-600 italic">
                    لَم يَخرَج الذَّكاء بِاختِيار. راجِع البَدائِل يَدَوِياً.
                  </div>
                )}
              </div>

              <div className="rounded-xl border bg-white p-3" data-testid="ai-reasoning">
                <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">
                  التَّفسير
                </div>
                <p className="text-sm leading-relaxed text-slate-800">
                  {data.reasoning_ar || <em className="text-slate-400">لا يَوجَد تَفسير.</em>}
                </p>
              </div>

              {data.evidence?.length > 0 && (
                <div data-testid="ai-evidence">
                  <div className="text-[10px] uppercase font-semibold text-slate-500 mb-2">
                    الأَدِلَّة ({data.evidence.length})
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {data.evidence.map((ev, i) => (
                      <EvidenceChip key={i} ev={ev} />
                    ))}
                  </div>
                </div>
              )}

              <div className="text-[10px] text-slate-400 text-center font-mono">
                {data.model}
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t bg-slate-50 flex items-center justify-between gap-2">
          <button onClick={ask} disabled={busy}
                  data-testid="ai-retry"
                  className="text-xs font-semibold rounded-lg bg-white border border-slate-300 hover:bg-slate-100 px-3 py-2 disabled:opacity-50">
            استِشارَة مَرَّة أُخرى
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} data-testid="ai-cancel"
                    className="text-sm font-semibold rounded-lg bg-white border border-slate-300 hover:bg-slate-100 px-4 py-2">
              إلغاء
            </button>
            {data?.available && data?.pick_candidate_id && (
              <button onClick={accept} disabled={accepting}
                      data-testid="ai-accept"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 disabled:opacity-50">
                <Check className="w-4 h-4" /> {accepting ? '…' : 'اقبَل الاقتِراح'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
